import React, { useState } from "react";
import { Label } from "react-aria-components";
import { Label as SemanticLabel } from "semantic-ui-react";
import { NumberCondition } from "../../queries/getCrmRequests";
import { Dropdown, Icon, Input } from "semantic-ui-react";
import { IBaseControlProps, nestedErrorMessageSelector } from "./Utils";
import { Controller } from "react-hook-form";
import "./NumberFilter.scss";

const NumberOperatorOptions: {
  key: NumberCondition;
  text: string;
  value: NumberCondition;
}[] = [
  { key: "Equal", text: "Is Equal To", value: "Equal" },
  { key: "NotEqual", text: "Is Not Equal To", value: "NotEqual" },
  { key: "GreaterThan", text: "Is Greater Than", value: "GreaterThan" },
  { key: "LessThan", text: "Is Less Than", value: "LessThan" },
  { key: "GreaterThanOrEqual", text: "Is Greater Than Or Equal To", value: "GreaterThanOrEqual" },
  { key: "LessThanOrEqual", text: "Is Less Than Or Equal To", value: "LessThanOrEqual" },
  { key: "Between", text: "Is Between", value: "Between" },
  { key: "Empty", text: "Is Empty", value: "Empty" },
  { key: "NotEmpty", text: "Is Not Empty", value: "NotEmpty" },
];
interface INumberFilterProps {
  label: string;
  defaultValues?: {
    firstNumber?: number;
    secondNumber?: number;
  };
  defaultOperator?: NumberCondition;
  className?: string;
  useEmptyOperator?: boolean;
  onChange?: (values: { firstNumber?: number; secondNumber?: number }) => void;
  onOperatorChange?: (
    operator: NumberCondition,
    values: {
      firstNumber?: number;
      secondNumber?: number;
    },
  ) => void;
  numberFieldType: "Number" | "Currency" | "Percent";
}

export const NumberFilter = ({
  label,
  defaultValues = {},
  defaultOperator = "Equal",
  className,
  useEmptyOperator = true,
  onChange,
  onOperatorChange,
  numberFieldType,
}: INumberFilterProps) => {
  const [values, setValues] = useState<{
    firstNumber?: number;
    secondNumber?: number;
  }>(defaultValues);
  const [operator, setOperator] = useState<NumberCondition>(defaultOperator);
  const shouldRenderInput = operator !== "Empty" && operator !== "NotEmpty";
  const shouldRenderSecondInput = operator === "Between";
  const inputLabel = numberFieldType === "Currency" ? "$" : numberFieldType === "Percent" ? "%" : null;
  const isInteger = numberFieldType === "Number";

  return (
    <div className={className}>
      <div className='tw-flex tw-w-full tw-items-center'>
        <Label className='tw-font-bold'>{label}</Label>
        <Dropdown
          id='String-range-operator-dropdown'
          className='tw-ml-4 tw-font-bold tw-text-blue'
          options={
            useEmptyOperator
              ? NumberOperatorOptions
              : NumberOperatorOptions.filter((option) => option.value !== "Empty" && option.value !== "NotEmpty")
          }
          defaultValue={operator}
          onChange={(_, { value }) => {
            setOperator(value as NumberCondition);
            if (onOperatorChange) {
              if (value === "Empty" || value === "NotEmpty") {
                onOperatorChange(value as NumberCondition, {});
                return;
              }
              if (value === "Between") {
                setValues((prev) => ({
                  firstNumber: prev.firstNumber ?? undefined,
                  secondNumber: prev.secondNumber ?? undefined,
                }));
                onOperatorChange(value as NumberCondition, {
                  firstNumber: values.firstNumber,
                  secondNumber: values.secondNumber,
                });
                return;
              }
              setValues((prev) => ({
                firstNumber: prev.firstNumber ?? undefined,
                secondNumber: undefined,
              }));
              onOperatorChange(value as NumberCondition, {
                firstNumber: values.firstNumber,
                secondNumber: undefined,
              });
            }
          }}
        />
      </div>
      {shouldRenderInput && (
        <div className='number-filter tw-mt-2 tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-0 tw-shadow-none '>
          <Input
            className='tw-w-full'
            type='number'
            label={inputLabel}
            labelPosition='left'
            defaultValue={values.firstNumber}
            value={values.firstNumber}
            onKeyDown={(e: { key: any; preventDefault: Function }) => {
              if (isInteger) {
                if (
                  e.key === "." ||
                  e.key === "e" ||
                  e.key === "E" ||
                  (e.key === "-" && values.firstNumber !== undefined)
                ) {
                  e.preventDefault(); // Prevent decimal and scientific notation for integer input
                  return;
                }
              }
            }}
            onChange={(_, data) => {
              const newValue = data.value ? parseFloat(data.value) : undefined;
              const secondNumber = shouldRenderSecondInput ? values.secondNumber : undefined;
              setValues({
                firstNumber: newValue,
                secondNumber: values.secondNumber, //preserve second number if it exists
              });
              onChange?.({
                firstNumber: newValue,
                secondNumber: secondNumber, //but don't send it to parent component if not between operator
              });
            }}
          />
          {shouldRenderSecondInput && (
            <>
              <Icon className='tw-m-2' name={"arrows alternate horizontal"} size='large' />
              <Input
                className='tw-w-full'
                type='number'
                defaultValue={values.secondNumber}
                label={inputLabel}
                labelPosition='left'
                value={values.secondNumber}
                onKeyDown={(e: { key: any; preventDefault: Function }) => {
                  if (isInteger) {
                    if (
                      e.key === "." ||
                      e.key === "e" ||
                      e.key === "E" ||
                      (e.key === "-" && values.secondNumber !== undefined)
                    ) {
                      e.preventDefault(); // Prevent decimal and scientific notation for integer input
                      return;
                    }
                  }
                }}
                onChange={(_, data) => {
                  const newValue = data.value ? parseFloat(data.value) : undefined;
                  setValues((prev) => ({
                    ...prev,
                    secondNumber: newValue,
                  }));
                  onChange?.({
                    firstNumber: values.firstNumber,
                    secondNumber: newValue,
                  });
                }}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

interface IControlledNumberFilterProps extends IBaseControlProps, INumberFilterProps {}
export const ControlledNumberFilter = ({ control, name, errors, ...props }: IControlledNumberFilterProps) => {
  const errorMessage = nestedErrorMessageSelector(errors)(name);
  return (
    <Controller
      control={control}
      name={name}
      errors={errors}
      defaultValue={{
        numberValue: props.defaultValues || {
          firstNumber: undefined,
          secondNumber: undefined,
        },
        numberCondition: props.defaultOperator || "Equal",
      }}
      render={({ onChange, value: formValue }) => (
        <>
          <NumberFilter
            {...props}
            defaultValues={formValue?.jsonValue || props.defaultValues}
            defaultOperator={formValue?.numberCondition || props.defaultOperator}
            onChange={(values) => {
              onChange({
                ...formValue,
                numberValue: values,
              });
              if (props.onChange) {
                props.onChange(values);
              }
            }}
            onOperatorChange={(operator, values) => {
              onChange({
                ...formValue,
                numberCondition: operator,
                numberValue: values,
              });
              if (props.onOperatorChange) {
                props.onOperatorChange(operator, values);
              }
            }}
          />
          {!!errorMessage && (
            <SemanticLabel className='tw-ml-0' basic prompt pointing='above'>
              {errorMessage}
            </SemanticLabel>
          )}
        </>
      )}
    />
  );
};
